import React, { useState } from 'react';
import { Badge, Box, Drawer, IconButton, Stack, Typography } from '@mui/material';
import { Close, FilterList } from '@mui/icons-material';
import { MuiButton } from '@ace/ace-ui-web';

interface FiltersDrawerProps {
    /** Title shown at the top of the drawer. */
    title?: string;
    /** Number of active filters — shown as a badge on the trigger button. */
    activeCount?: number;
    /** Trigger button label. */
    triggerLabel?: string;
    children: React.ReactNode;
}

/**
 * Mobile-friendly filter container. Renders a trigger button that opens a
 * bottom-anchored drawer containing the provided filter controls.
 */
const FiltersDrawer: React.FC<FiltersDrawerProps> = ({
    title = 'Filters',
    activeCount = 0,
    triggerLabel = 'Filters',
    children,
}) => {
    const [open, setOpen] = useState(false);

    return (
        <>
            <MuiButton
                colors="secondary"
                variant="outlined"
                onClick={() => setOpen(true)}
            >
                <Stack direction="row" alignItems="center" spacing={0.75}>
                    <Badge badgeContent={activeCount} color="error" invisible={!activeCount}>
                        <FilterList fontSize="small" />
                    </Badge>
                    <span>{triggerLabel}</span>
                </Stack>
            </MuiButton>

            <Drawer
                anchor="bottom"
                open={open}
                onClose={() => setOpen(false)}
                PaperProps={{
                    sx: {
                        borderTopLeftRadius: 16,
                        borderTopRightRadius: 16,
                        maxHeight: '85vh',
                        pb: 'env(safe-area-inset-bottom)',
                    },
                }}
            >
                <Box sx={{ p: 2 }}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ mb: 1.5 }}
                    >
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {title}
                        </Typography>
                        <IconButton
                            onClick={() => setOpen(false)}
                            size="large"
                            aria-label="Close filters"
                        >
                            <Close />
                        </IconButton>
                    </Stack>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {children}
                    </Box>
                    <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                        <MuiButton
                            colors="acePrimary"
                            onClick={() => setOpen(false)}
                            sx={{ width: '100%' }}
                        >
                            Done
                        </MuiButton>
                    </Stack>
                </Box>
            </Drawer>
        </>
    );
};

export default FiltersDrawer;
